// SYSTEM_ROUTE: /targets
import { useState } from 'react';
import type { ConsistencySummary, Target, TargetCadence, TargetType } from '@life-os/app-core';
import { Button, Chip, CompactRow, EmptyState, Pane, Stack } from '@life-os/ui';
import type { ChipTone } from '@life-os/ui';
import { useServices, useStore } from '../app/store.js';
import { useAsync } from '../lib/useAsync.js';
import { ScreenHeader } from '../app/ScreenHeader.js';

const TYPES: TargetType[] = [
  'output_target',
  'practice_target',
  'learning_target',
  'growth_target',
  'maintenance_target',
  'repair_target',
  'outcome_target',
];

const CADENCES: TargetCadence[] = ['daily', 'weekly', 'monthly'];

const STATE_TONE: Record<ConsistencySummary['state'], ChipTone> = {
  on_track: 'primary',
  in_progress: 'neutral',
  needs_attention: 'warn',
  paused: 'muted',
};

const STATE_LABEL: Record<ConsistencySummary['state'], string> = {
  on_track: 'On track',
  in_progress: 'In progress',
  needs_attention: 'Needs attention',
  paused: 'Paused',
};

interface Row {
  target: Target;
  summary: ConsistencySummary;
}

export function TargetsScreen() {
  const services = useServices();
  const { revision, refresh } = useStore();
  const [title, setTitle] = useState('');
  const [type, setType] = useState<TargetType>('output_target');
  const [cadence, setCadence] = useState<TargetCadence>('daily');
  const [quantity, setQuantity] = useState(1);

  const rows = useAsync<Row[]>(async () => {
    const targets = await services.targets.list();
    return Promise.all(
      targets.map(async (target) => ({ target, summary: await services.targets.consistency(target.id) })),
    );
  }, [revision]);

  const create = async () => {
    if (title.trim().length === 0) return;
    await services.targets.create({ title, targetType: type, cadence, quantity });
    setTitle('');
    setQuantity(1);
    refresh();
  };

  const log = async (target: Target) => {
    await services.targets.logEvidence(target.id);
    refresh();
  };

  const togglePause = async (r: Row) => {
    await services.targets.setStatus(r.target.id, r.target.status === 'paused' ? 'active' : 'paused');
    refresh();
  };

  const items = rows.value ?? [];

  const selectStyle = {
    background: 'var(--lc-surface-container)',
    color: 'var(--lc-on-surface)',
    border: '1px solid var(--lc-outline-variant)',
    borderRadius: 'var(--lc-radius-full)',
    padding: '6px 12px',
    font: 'inherit',
    fontSize: 'var(--lc-text-sm)',
  } as const;

  return (
    <>
      <ScreenHeader title={<>Targets & Streaks</>} subtitle={<>Measured commitments with evidence. Misses prompt review, never shame.</>} />

      <Pane title="New target" hint="Cadence + quantity + what counts as honest evidence.">
        <div style={{ display: 'flex', gap: 'var(--lc-stack-sm)', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            className="lc-cmdbar"
            style={{ flex: '1 1 240px' }}
            value={title}
            placeholder="e.g. Publish one writeup"
            onChange={(e) => setTitle(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void create();
            }}
            aria-label="Target title"
          />
          <select value={type} onChange={(e) => setType(e.target.value as TargetType)} aria-label="Type" style={selectStyle}>
            {TYPES.map((t) => (
              <option key={t} value={t}>
                {t.replace(/_target$/, '').replace(/_/g, ' ')}
              </option>
            ))}
          </select>
          <select value={cadence} onChange={(e) => setCadence(e.target.value as TargetCadence)} aria-label="Cadence" style={selectStyle}>
            {CADENCES.map((c) => (
              <option key={c} value={c}>
                {c}
              </option>
            ))}
          </select>
          <input
            type="number"
            min={1}
            value={quantity}
            onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
            aria-label="Quantity per window"
            style={{ ...selectStyle, width: 72 }}
          />
          <Button variant="primary" onClick={() => void create()}>
            Add target
          </Button>
        </div>
      </Pane>

      <Pane title="Active targets" hint={`${items.length}`}>
        <Stack>
          {items.length === 0 ? (
            <EmptyState title="No targets yet">
              Turn a goal into one repeatable, evidence-backed commitment. Keep them few.
            </EmptyState>
          ) : (
            items.map(({ target, summary }) => (
              <CompactRow
                key={String(target.id)}
                title={target.title}
                subtitle={`${target.quantity}× per ${target.cadence} · ${summary.currentWindowCount}/${target.quantity} this ${target.cadence} · streak ${summary.streakWindows}`}
                meta={
                  <>
                    <Chip tone={STATE_TONE[summary.state]}>{STATE_LABEL[summary.state]}</Chip>
                    <Button variant="ghost" onClick={() => void togglePause({ target, summary })}>
                      {target.status === 'paused' ? 'Resume' : 'Pause'}
                    </Button>
                    <Button variant="primary" onClick={() => void log(target)}>
                      + Evidence
                    </Button>
                  </>
                }
              />
            ))
          )}
        </Stack>
      </Pane>
    </>
  );
}
